import {Platform, Text, View} from 'react-native';
import React, {useRef, useState} from 'react';
import {useTheme} from 'contexts/ThemeContext';
import PickerButton from 'components/molecules/pickerButton';
import DaysBar from 'components/molecules/daysBar';
import LongRectangleTextButton from 'components/atoms/longRectangleTextButton';
import {DURATION_DATA_FOR_WEB, WEEK_DAYS} from 'constants/constants';
import strings from '../../../translations';
import DaysList from 'components/molecules/daysList';
import CalendarModal, {
  CalendarModalHandle,
} from 'components/molecules/modalCalendar';
import {timeToString} from 'helpers/timeToString';
import {TDay} from 'stores/CreateClassStore';
import {useCreateClassStore} from 'contexts/StoreContext';
import {durationToMinutes} from 'helpers/durationToMinutes';
import {minsConvertToHourAndMins} from 'helpers/minsConvertToHourAndMins';
import {dateFormatDDMMYYY} from 'helpers/dateFormatDDMMYYY';
import {generateAWeekRangeForPicker} from 'constants/calendarConstants';
import styleSheet from './styles';
import {createArrayOfTime} from 'helpers/createArrayOfTime';
import {useNavigation} from '@react-navigation/native';
import Picker, {PickerHandle} from 'components/molecules/picker';
import {getISODayWithoutHours} from 'helpers/getISODayWithoutHours';

export interface CreateClassProps {
  subject: string;
  language: string;
  group: string;
  startDate: string;
  endDate: string;
  duration: string | number;
  subjectData: string[];
  languageData: string[];
  groupData: string[];
  selectedDays: TDay[];
  day: string;
  time: string;
  onPressCreateClass(): void;
}

export enum PickerType {
  subject,
  language,
  group,
  time,
  startDate,
  endDate,
  duration,
  analyticType,
  student,
}

const CreateClass = (props: CreateClassProps) => {
  const styles = styleSheet(useTheme());
  const theme = useTheme();
  const navigation = useNavigation();

  const createClassStore = useCreateClassStore();

  const [typePicker, setTypePicker] = useState<PickerType | null>(null);
  const [timePickerValue, setTimePickerValue] = useState<string>(
    timeToString(new Date()),
  );
  const [hour, setHour] = useState<string | Date>('0');
  const [minutes, setMinutes] = useState<string | Date>('15');
  const [dayForDelete, setDayForDelete] = useState<string>('');

  const pickerRef = useRef<PickerHandle>(null);
  const startCalendarRef = useRef<CalendarModalHandle>(null);
  const endCalendarRef = useRef<CalendarModalHandle>(null);

  const arrOfTime = createArrayOfTime();

  const onHandlePicker = () => {
    if (pickerRef.current) {
      pickerRef.current.switchVisible();
    }
  };

  const onPressSubjectButton = () => {
    onHandlePicker();

    setTypePicker(PickerType.subject);
  };

  const onPressLanguageButton = () => {
    onHandlePicker();

    setTypePicker(PickerType.language);
  };

  const onPressGroupButton = () => {
    onHandlePicker();

    setTypePicker(PickerType.group);
  };

  const onPressStartDateButton = () => {
    startCalendarRef.current?.switchVisible();

    setTypePicker(PickerType.startDate);
  };

  const chooseStartDate = (value: string) => {
    const weekRange = generateAWeekRangeForPicker(value);

    createClassStore.setStartDate(value);
    createClassStore.setEndDate(weekRange);
  };

  const onPressEndDateButton = () => {
    endCalendarRef.current?.switchVisible();

    setTypePicker(PickerType.endDate);
  };

  const onPressDurationButton = () => {
    onHandlePicker();

    setTypePicker(PickerType.duration);
  };

  const onPressDayButton = (day: string) => {
    if (Platform.OS !== 'web') {
      onHandlePicker();
      setTypePicker(PickerType.time);
      setDayForDelete(day);
    } else {
      createClassStore.setUpArrayOfTimes(day, arrOfTime[0]);
    }
  };

  const onPressDeleteTime = (day: string, time: string) => {
    createClassStore.deleteTimeFromArray(day, time);
  };

  const pickerButtons = (
    <>
      <PickerButton
        pickerLabel={strings.createClassScreen.subject}
        onPress={onPressSubjectButton}
        icon={theme.images.subjectIcon}
        pickerValue={props.subject}
        downArowIcon
        pickerContainerStyle={styles.pickerContainerStyle}
        data={props.subjectData}
        onValueChange={value => createClassStore.setSubject(value)}
        zIndex={styles.zIndex7}
      />

      <PickerButton
        pickerLabel={strings.createClassScreen.language}
        onPress={onPressLanguageButton}
        icon={theme.images.languageIcon}
        pickerValue={props.language}
        downArowIcon
        pickerContainerStyle={styles.pickerContainerStyle}
        data={props.languageData}
        onValueChange={value => createClassStore.setLanguage(value)}
        zIndex={styles.zIndex6}
      />

      <PickerButton
        pickerLabel={strings.createClassScreen.group}
        onPress={onPressGroupButton}
        icon={theme.images.classIcon}
        pickerValue={props.group}
        downArowIcon
        pickerContainerStyle={styles.pickerContainerStyle}
        data={props.groupData}
        onValueChange={value => createClassStore.setGroup(value)}
        zIndex={styles.zIndex5}
      />

      <PickerButton
        startDate={new Date(props.startDate)}
        endDate={new Date(props.endDate)}
        pickerLabel={strings.createClassScreen.startDate}
        onPress={onPressStartDateButton}
        icon={theme.images.calendarIcon}
        pickerValue={dateFormatDDMMYYY(props.startDate)}
        pickerContainerStyle={styles.pickerContainerStyle}
        downArowIcon={Platform.OS === 'web'}
        isWebCalendar
        zIndex={styles.zIndex4}
        onDayPress={value =>
          createClassStore.setStartDate(getISODayWithoutHours(value))
        }
      />

      <PickerButton
        endDate={new Date(generateAWeekRangeForPicker(props.startDate))}
        pickerLabel={strings.createClassScreen.endDate}
        onPress={onPressEndDateButton}
        icon={theme.images.calendarIcon}
        pickerValue={dateFormatDDMMYYY(props.endDate)}
        pickerContainerStyle={styles.pickerContainerStyle}
        downArowIcon={Platform.OS === 'web'}
        zIndex={styles.zIndex3}
        onDayPress={value =>
          createClassStore.setEndDate(getISODayWithoutHours(value))
        }
        isWebCalendar
        isSecondCalendar
      />

      <PickerButton
        pickerLabel={strings.createClassScreen.duration}
        onPress={onPressDurationButton}
        icon={theme.images.clockIcon}
        pickerValue={minsConvertToHourAndMins(props.duration)}
        pickerContainerStyle={styles.pickerContainerStyle}
        downArowIcon={Platform.OS === 'web'}
        data={DURATION_DATA_FOR_WEB}
        onValueChange={value => createClassStore.setDuration(Number(value))}
        zIndex={styles.zIndex2}
        dropDownListStyle={styles.dropDownListStyle}
        isDurationList
      />
    </>
  );

  const daysPicker = (
    <>
      <DaysBar
        data={WEEK_DAYS}
        selectedDays={props.selectedDays}
        setSelectedDays={value => createClassStore.setDays(value)}
        onPress={onPressDayButton}
        onDayChanged={value => createClassStore.setDay(value)}
      />

      <DaysList
        timesDateData={props.selectedDays}
        onPressAdd={onPressDayButton}
        onPressDelete={onPressDeleteTime}
        onDayChanged={value => createClassStore.setDay(value)}
        onTimeChanged={value => createClassStore.setTime(value)}
        onValueChange={(day, index, value) =>
          createClassStore.timeEditInDay(day, index, value)
        }
      />
    </>
  );

  const disabled = createClassStore.days.length === 0;

  const submitButtonsContainer = (
    <View style={styles.containerButtonStyle}>
      <LongRectangleTextButton
        title={strings.createClassScreen.createClass}
        onPress={props.onPressCreateClass}
        buttonStyle={styles.createButtonStyle}
        disabled={disabled}
        backgroundColor={
          disabled ? theme.colors.alto : theme.colors.cornflowerBlue
        }
      />

      {Platform.OS === 'web' && (
        <LongRectangleTextButton
          title={strings.shared.cancel}
          onPress={() => navigation.goBack()}
          buttonStyle={styles.cancelButtonStyle}
          titleColor={styles.cancelTitleButtonStyle}
        />
      )}
    </View>
  );

  const createPicker = (pickerType: PickerType | null) => {
    switch (pickerType) {
      case PickerType.subject:
        return (
          <Picker
            ref={pickerRef}
            pickerTitle={strings.createClassScreen.chooseSubjectTitle}
            pickerData={props.subjectData}
            selectedValue={props.subject}
            onChangeValue={value => createClassStore.setSubject(value)}
            secondPickerOnChangeValue={() => {}}
            secondPickerData={[]}
          />
        );

      case PickerType.language:
        return (
          <Picker
            ref={pickerRef}
            pickerTitle={strings.createClassScreen.chooseLanguageTitle}
            pickerData={props.languageData}
            selectedValue={props.language}
            onChangeValue={value => createClassStore.setLanguage(value)}
            secondPickerOnChangeValue={() => {}}
            secondPickerData={[]}
          />
        );

      case PickerType.group:
        return (
          <Picker
            ref={pickerRef}
            pickerTitle={strings.createClassScreen.chooseGroupTitle}
            pickerData={props.groupData}
            selectedValue={props.group}
            onChangeValue={value => createClassStore.setGroup(value)}
            secondPickerOnChangeValue={() => {}}
            secondPickerData={[]}
          />
        );

      case PickerType.time:
        return (
          <Picker
            ref={pickerRef}
            onPress={() =>
              createClassStore.setUpArrayOfTimes(props.day, timePickerValue)
            }
            pickerTitle={strings.createClassScreen.addTime}
            onChangeValue={value => setTimePickerValue(timeToString(value))}
            secondPickerOnChangeValue={() => {}}
            isTimePicker
            pickerData={[]}
            secondPickerData={[]}
            onPressOut={() => createClassStore.deleteDayFromArray(dayForDelete)}
          />
        );

      case PickerType.duration:
        return (
          <Picker
            ref={pickerRef}
            onPress={() =>
              createClassStore.setDuration(durationToMinutes(hour, minutes))
            }
            pickerTitle={strings.createClassScreen.chooseDurationTitle}
            selectedValue={hour}
            onChangeValue={value => setHour(value)}
            secondPickerSelectedValue={minutes}
            secondPickerOnChangeValue={value => setMinutes(value)}
            isTwoColumnsPicker
            pickerData={[]}
            secondPickerData={[]}
          />
        );

      default:
        return (
          <Picker
            ref={pickerRef}
            pickerTitle=""
            pickerData={[]}
            selectedValue=""
            onChangeValue={() => {}}
            secondPickerOnChangeValue={() => {}}
            secondPickerData={[]}
          />
        );
    }
  };

  const createDatePicker = (pickerType: PickerType | null, index: number) => {
    switch (pickerType) {
      case PickerType.startDate:
        return (
          <CalendarModal
            key={index}
            modalWindowStyle={styles.calendarStyle}
            ref={startCalendarRef}
            selected={createClassStore.startDate}
            onPress={(value: string) => chooseStartDate(value)}
            buttonLabel={strings.createClassScreen.chooseDay}
            minDate={new Date()}
          />
        );

      case PickerType.endDate:
        return (
          <CalendarModal
            key={index}
            modalWindowStyle={styles.calendarStyle}
            ref={endCalendarRef}
            selected={createClassStore.endDate}
            minDate={
              new Date(generateAWeekRangeForPicker(createClassStore.startDate))
            }
            onPress={(value: string) => {
              createClassStore.setEndDate(value);
            }}
            buttonLabel={strings.createClassScreen.chooseDay}
          />
        );

      default:
        return <View />;
    }
  };

  return (
    <View style={styles.containerStyle}>
      {Platform.OS === 'web' && (
        <Text style={styles.webTitleStyle}>
          {strings.createClassScreen.scheduleClass}
        </Text>
      )}

      {pickerButtons}

      {daysPicker}

      {submitButtonsContainer}

      {createPicker(typePicker)}

      {[PickerType.startDate, PickerType.endDate].map((type, index) =>
        createDatePicker(type, index),
      )}
    </View>
  );
};

export default CreateClass;
